import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  appendNoDuplicateRecordNumberRefine,
  documentSchema,
} from "@/api/document/document-validation";
import {
  getRelativePosition,
  useGetDocument,
  useUpdateDocument,
} from "@/api/document/documents";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldDocumentSummary } from "@/lib/computeFields";
import { Document } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(
  documentSchema.passthrough(),
);

const EditDocument = ({ document }: { document: Document }) => {
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: document,
  });

  const [, setSearchParams] = useSearchParams();

  const { data: attachments } = useGetAttachmentsByDocId(document.id);

  const documentUpdater = useUpdateDocument();

  const { t } = useTranslation();

  const handleResume = (data: any) => {
    return data
      ? computeFieldDocumentSummary(data, { language: getMainLanguage() })
      : "";
  };

  const queryClient = useQueryClient();

  const { relativePosition } = useDashboardParams<Document>({
    statusField: "documentsPublicationStatus",
    resourceName: "document",
    relativePosition: {
      currentId: document.id,
      positionFetcher: getRelativePosition,
    },
  });

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            documentUpdater.mutate(
              {
                ...values,
                ...(status && { documentsPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("documentSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.documentsDescriptionNumeroEnregistrement != undefined)
              setSearchParams({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["documents"],
        });
        queryClient.invalidateQueries({
          queryKey: ["document", document.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: document.id }],
        });
      }}
      relativePosition={relativePosition}
      publicationStatus={document.documentsPublicationStatus}
      title={document.documentsDescriptionTitre}
      reference={document.documentsDescriptionNumeroEnregistrement}
      id={document.id}
      type="document"
      picturesData={attachments && { pictures: attachments, type: "document" }}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={
          [
            {
              value: "description",
              label: t("documentDescription"),
            },
            {
              value: "retranscription",
              label: t("retranscription"),
            },
            {
              value: "sources",
              label: t("sources"),
            },
            {
              value: "files",
              label: t("fichiers"),
            },
            {
              value: "links",
              label: t("linkedEntries"),
            },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row>
              <AF.Text
                name="documentsDescriptionNumeroEnregistrement"
                className="w-64"
              />
              <AF.Checkbox name="documentsDescriptionCopies" align={true} />
            </AF.Row>
            <ModifiableList
              name="documentsDescriptionType"
              listKey="liste-types-document"
            />
            <AF.Row>
              <AF.Text name="documentsDescriptionClassement" />
              <AF.Text name="documentsDescriptionRangement" />
            </AF.Row>
            <ModifiableList
              name="documentsDescriptionLocalisation"
              listKey="liste-bibliotheques-archives"
            />
            <AF.Text
              className="w-16"
              name="documentsDescriptionNombreExemplairesArchives"
            />
            <AF.Text name="documentsDescriptionTitre" />
            <AF.Row>
              <AF.Text name="documentsDescriptionDate" />
              <ModifiableList
                name="documentsDescriptionTheme"
                listKey="liste-themes"
              />
            </AF.Row>
            <TwoDimensionalInput parentName="documentsDescriptionDimensions" />
            <ModifiableList
              name="documentsDescriptionTechnique"
              listKey="liste-techniques-document"
            />
            <AF.Text name="documentsDescriptionLieuDeCreation" />
            <ModifiableList
              name="documentsDescriptionPhotographe"
              listKey="liste-photographes"
            />
            <ModifiableList
              name="documentsDescriptionCopyright"
              listKey="liste-credits"
            />
            <AF.Text
              className="w-16"
              name="documentsDescriptionNombreFeuillets"
            />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="documentsDescriptionLegendeRedigee"
              computeAction={handleResume}
            />
            <AF.RichText name="documentsDescriptionNotes" />
            <AF.Row>
              <AF.Checkbox name="documentsFichierPhotoBasseDefinitionUniquement" />
              <AF.Checkbox name="documentsFichierPhotoScanAFaire" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="documentsFichierPhotoOkWeb" />
              <AF.Checkbox name="documentsFichierPhotoHdOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="documentsValidationPublicationNumerisee" />
              <AF.Checkbox name="documentsValidationPdfEtOcrOk" />
            </AF.Row>
            <Separator />

            <AF.RadioGroup
              items={[
                { value: "documentsAcquisitionDocumentAAcquerir" },
                { value: "documentsAcquisitionDocumentAcquis" },
              ]}
            />

            <AF.Row>
              <AF.Text name="documentsAcquisitionDateAcquisition" />
              <AF.Text name="documentsAcquisitionVendeur" />
            </AF.Row>
            <AF.RichText name="documentsAcquisitionNotes" />
            <AF.Text name="documentsMouvementsDepot" />
            <AF.RichText name="documentsMouvementsNotesDepot" />
            <AF.LegacySelectCurrency name="documentsMouvementsValeurAssurance" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="retranscription">
          <AF.RichText name="documentsDescriptionRetranscription" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={<AddFileModal id={document.id} />}
        >
          <AF.OthersFilesTable docId={document.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={document.id}
            type="document"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "documentInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={document}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditDocumentPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetDocument(id);

  return (
    <>
      {data && <EditDocument key={data.id} document={data} />}
      {!data && !isLoading && <h2>noDocumentLabel</h2>}
    </>
  );
};
